import React, { useState, useEffect, useRef } from 'react';
import style from './Reporting.module.css';
import { Table, Pagination, Spin, message, Button, Dropdown, Space, Tag } from 'antd';
import { DownOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FormInput } from '../../components/Basic/FormInput';
import * as Action from '../../store/slices/reporting';
import { connect } from 'react-redux';
import Header from '../../components/Header/header';
import { hasPermission } from '../../utils/premission';
import { PERMISSIONS } from '../../utils/constant/permissions';
import * as XLSX from 'xlsx';

interface GetData {
    SearchFilter: (payload: any) => unknown;
    ExportFile: (payload: any) => unknown;
    Reporting_Red: any;
}

interface Form {
    StartDate: string;
    EndDate: string;
    CampaignId: string;
}

interface ActiveFilters {
    startDate: string;
    endDate: string;
    campaignIds: number[];
}

const validationSchema = yup.object().shape({
    StartDate: yup.string().optional(),
    EndDate: yup.string().optional(),
    CampaignId: yup.string().optional(),
});

// ── Report type → columns mapping (per spec) ──────────────────────────────
const REPORT_COLUMNS: Record<string, { header: string; key: string }[]> = {
    BALANCE: [
        { header: 'Winner Name',             key: 'winnerName' },
        { header: 'Branch',                  key: 'branchIdentifier' },
        { header: 'Winner Account Number',   key: 'winnerAccountNumber' },
        { header: 'Customer Classification', key: 'customerClassification' },
        { header: 'Account Balance',         key: 'accountBalance' },
        { header: 'Draw Execution Date',     key: 'endDateTime' },
        { header: 'Campaign Name',           key: 'campaignName' },
        { header: 'Number of Entries',       key: 'numberofEntries' },
    ],
    FIRST_SALARY_TRANSFER: [
        { header: 'Winner Name',             key: 'winnerName' },
        { header: 'Branch',                  key: 'branchIdentifier' },
        { header: 'Winner Account Number',   key: 'winnerAccountNumber' },
        { header: 'Customer Classification', key: 'customerClassification' },
        { header: 'Salary Amount',           key: 'salaryAmount' },
        { header: 'Draw Execution Date',     key: 'endDateTime' },
        { header: 'Campaign Name',           key: 'campaignName' },
        { header: 'Number of Entries',       key: 'numberofEntries' },
    ],
    CC_SPENDING: [
        { header: 'Winner Name',                      key: 'winnerName' },
        { header: 'Branch',                           key: 'branchIdentifier' },
        { header: 'Winner Account Number',            key: 'winnerAccountNumber' },
        { header: 'Customer Classification',          key: 'customerClassification' },
        { header: 'Credit Card Masked Number',        key: 'ccMaskedNo' },
        { header: 'CC Spending for Campaign Period',  key: 'ccSpendingInPeriod' },
        { header: 'Draw Execution Date',              key: 'endDateTime' },
        { header: 'Campaign Name',                    key: 'campaignName' },
        { header: 'Number of Entries',                key: 'numberofEntries' },
    ],
    GET_CC: [
        { header: 'Winner Name',                  key: 'winnerName' },
        { header: 'Branch',                       key: 'branchIdentifier' },
        { header: 'Winner Account Number',        key: 'winnerAccountNumber' },
        { header: 'Customer Classification',      key: 'customerClassification' },
        { header: 'Credit Card Masked Number',    key: 'ccMaskedNo' },
        { header: 'Issuance Date of Credit Card', key: 'issDateCc' },
        { header: 'Draw Execution Date',          key: 'endDateTime' },
        { header: 'Campaign Name',                key: 'campaignName' },
        { header: 'Number of Entries',            key: 'numberofEntries' },
    ],
    DC_SPENDING: [
        { header: 'Winner Name',                     key: 'winnerName' },
        { header: 'Branch',                          key: 'branchIdentifier' },
        { header: 'Winner Account Number',           key: 'winnerAccountNumber' },
        { header: 'Customer Classification',         key: 'customerClassification' },
        { header: 'Debit Card Masked Number',        key: 'dcMaskedNo' },
        { header: 'DC Spending for Campaign Period', key: 'dcSpendingInPeriod' },
        { header: 'Draw Execution Date',             key: 'endDateTime' },
        { header: 'Campaign Name',                   key: 'campaignName' },
        { header: 'Number of Entries',               key: 'numberofEntries' },
    ],
};

const DEFAULT_COLUMNS: { header: string; key: string }[] = [
    { header: 'Campaign Name',      key: 'campaignName' },
    { header: 'Start Date',         key: 'startDate' },
    { header: 'End Date',           key: 'endDate' },
    { header: 'Winner Name',        key: 'winnerName' },
    { header: 'Branch',             key: 'branchIdentifier' },
    { header: 'Winner Account No',  key: 'winnerAccountNumber' },
    { header: 'Draw Execution Date',key: 'endDateTime' },
    { header: 'Campaign Status',    key: 'campaignStatus' },
    { header: 'Number of Entries',  key: 'numberofEntries' },
];

const formatCell = (val: any): string => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string' && val.includes('T')) return val.split('T')[0];
    return String(val);
};

// Winner columns shown in expanded row
const WINNER_TABLE_COLUMNS = [
    { title: 'Winner Name',              dataIndex: 'winnerName',             key: 'winnerName',             width: 140, render: (v: any) => v ?? '-' },
    { title: 'Branch',                   dataIndex: 'branchIdentifier',       key: 'branchIdentifier',       width: 100, render: (v: any) => v ?? '-' },
    { title: 'Account Number',           dataIndex: 'winnerAccountNumber',    key: 'winnerAccountNumber',    width: 150, render: (v: any) => v ?? '-' },
    { title: 'Classification',           dataIndex: 'customerClassification', key: 'customerClassification', width: 130, render: (v: any) => v ?? '-' },
    { title: 'Account Balance',          dataIndex: 'accountBalance',         key: 'accountBalance',         width: 120, render: (v: any) => v ?? '-' },
    { title: 'Salary Amount',            dataIndex: 'salaryAmount',           key: 'salaryAmount',           width: 120, render: (v: any) => v ?? '-' },
    { title: 'CC Masked No',             dataIndex: 'ccMaskedNo',             key: 'ccMaskedNo',             width: 140, render: (v: any) => v ?? '-' },
    { title: 'DC Masked No',             dataIndex: 'dcMaskedNo',             key: 'dcMaskedNo',             width: 140, render: (v: any) => v ?? '-' },
    { title: 'CC Spending',              dataIndex: 'ccSpendingInPeriod',     key: 'ccSpendingInPeriod',     width: 110, render: (v: any) => v ?? '-' },
    { title: 'DC Spending',              dataIndex: 'dcSpendingInPeriod',     key: 'dcSpendingInPeriod',     width: 110, render: (v: any) => v ?? '-' },
    { title: 'CC Issue Date',            dataIndex: 'issDateCc',              key: 'issDateCc',              width: 120, render: (v: any) => v ?? '-' },
    { title: 'No. of Entries',           dataIndex: 'numberofEntries',        key: 'numberofEntries',        width: 110, render: (v: any) => v ?? '-' },
];

const downloadCampaignReport = (row: any, format: 'csv' | 'xlsx') => {
    const reportType: string = row.reportType ?? '';
    const columns = REPORT_COLUMNS[reportType] ?? DEFAULT_COLUMNS;
    const fileName = `${row.campaignName ?? 'Campaign'}_${reportType || 'Report'}_${new Date().toISOString().split('T')[0]}`;
    const winners: any[] = Array.isArray(row.winners) ? row.winners : [];

    if (format === 'csv') {
        // Campaign info row
        const campaignHeaders = ['Campaign ID', 'Campaign Name', 'Start Date', 'End Date', 'Status', 'No Of Winners', 'Report Type'];
        const campaignRow = [row.campaignId, row.campaignName, row.startDate, row.endDate, row.campaignStatus, row.noOfWinners, row.reportType];

        // Winner rows
        const winnerHeaders = columns.map(c => c.header);
        const winnerRows = winners.map(w =>
            columns.map(c => {
                const v = formatCell(c.key === 'endDateTime' ? row.endDateTime : c.key === 'campaignName' ? row.campaignName : w[c.key]);
                return v.includes(',') ? `"${v}"` : v;
            })
        );

        const csv = [
            campaignHeaders.join(','),
            campaignRow.join(','),
            '',
            'Winners:',
            winnerHeaders.join(','),
            ...winnerRows.map(r => r.join(',')),
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${fileName}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success('CSV downloaded!');
    } else {
        const wb = XLSX.utils.book_new();

        // Sheet 1: Campaign info
        const campaignSheet = XLSX.utils.aoa_to_sheet([
            ['Campaign ID', 'Campaign Name', 'Start Date', 'End Date', 'Status', 'No Of Winners', 'Report Type'],
            [row.campaignId, row.campaignName, row.startDate, row.endDate, row.campaignStatus, row.noOfWinners, row.reportType],
        ]);
        XLSX.utils.book_append_sheet(wb, campaignSheet, 'Campaign Info');

        // Sheet 2: Winners
        const winnerHeaders = columns.map(c => c.header);
        const winnerRows = winners.map(w =>
            columns.map(c => formatCell(c.key === 'endDateTime' ? row.endDateTime : c.key === 'campaignName' ? row.campaignName : w[c.key]))
        );
        const winnerSheet = XLSX.utils.aoa_to_sheet([winnerHeaders, ...winnerRows]);
        winnerSheet['!cols'] = columns.map(() => ({ wch: 22 }));
        XLSX.utils.book_append_sheet(wb, winnerSheet, 'Winners');

        XLSX.writeFile(wb, `${fileName}.xlsx`);
        message.success('Excel downloaded!');
    }
};

const MultiViewTable: React.FC<GetData> = ({ SearchFilter, Reporting_Red, ExportFile }) => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(9);
    const [spinning, setSpinning] = useState(false);
    const [tableData, setTableData] = useState<any[]>([]);
    const [totalRecords, setTotalRecords] = useState<number>(0);

    // use a ref so the page-change effect always reads the latest filters
    const filtersRef = useRef<ActiveFilters>({ startDate: '', endDate: '', campaignIds: [] });

    useEffect(() => {
        const status = Reporting_Red?.dataSingle?.[0]?.res?.status;
        if (status === '1004') { localStorage.clear(); navigate('/'); }
    }, [Reporting_Red]);

    const { control, formState: { errors }, handleSubmit, getValues, setValue } = useForm<Form>({
        defaultValues: { StartDate: '', EndDate: '', CampaignId: '' },
        mode: 'onChange',
        resolver: yupResolver(validationSchema) as any,
    });

    const parseCampaignIds = (raw: string): number[] =>
        (raw || '').split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));

    const fetchData = async (filters: ActiveFilters, page: number, size: number) => {
        setSpinning(true);
        try {
            const response: any = await SearchFilter({
                startDate: filters.startDate || undefined,
                endDate: filters.endDate || undefined,
                campaignIds: filters.campaignIds.length > 0 ? filters.campaignIds : undefined,
                isExport: false,
                pageIndex: page - 1,
                pageRecord: size,
            });

            if (response?.status === '1004') { localStorage.clear(); navigate('/'); return; }
            if (response?.status === '1005') { message.warning('No data available'); setTableData([]); setTotalRecords(0); return; }

            if (response?.data) {
                setTableData(response.data);
                setTotalRecords(response.noOfRecord ?? response.data.length);
            } else {
                setTableData([]);
                setTotalRecords(0);
            }
        } catch {
            message.error('Failed to fetch data');
        } finally {
            setSpinning(false);
        }
    };

    // single effect — fires on page/size change and on initial mount
    useEffect(() => {
        fetchData(filtersRef.current, currentPage, pageSize);
    }, [currentPage, pageSize]);

    const onSearch = (data: Form) => {
        const filters: ActiveFilters = {
            startDate: data.StartDate,
            endDate: data.EndDate,
            campaignIds: parseCampaignIds(data.CampaignId),
        };
        filtersRef.current = filters;   // update ref synchronously before fetch
        setCurrentPage(1);              // if already page 1, effect won't re-fire
        fetchData(filters, 1, pageSize);
    };

    const FilterClear = () => {
        setValue('StartDate', '');
        setValue('EndDate', '');
        setValue('CampaignId', '');
        const empty: ActiveFilters = { startDate: '', endDate: '', campaignIds: [] };
        filtersRef.current = empty;
        setCurrentPage(1);
        fetchData(empty, 1, pageSize);
    };

    const handleExport = async (format: 'csv' | 'xls') => {
        if (tableData.length === 0) {
            message.warning('No data to export. Please search first.');
            return;
        }

        setSpinning(true);
        try {
            const fileName = `Campaign_Report_${new Date().toISOString().split('T')[0]}`;

            if (format === 'csv') {
                // Campaign-level rows + winner rows flattened
                const rows: string[][] = [];
                const headers = [
                    'Campaign ID', 'Campaign Name', 'Start Date', 'End Date',
                    'Status', 'No Of Winners', 'Report Type', 'Created By', 'Approved By',
                    'Winner Name', 'Branch', 'Account Number', 'Classification',
                    'Account Balance', 'Salary Amount', 'CC Masked No', 'DC Masked No',
                    'CC Spending', 'DC Spending', 'CC Issue Date', 'No. of Entries',
                ];
                rows.push(headers);

                tableData.forEach((campaign: any) => {
                    const winners: any[] = Array.isArray(campaign.winners) && campaign.winners.length > 0
                        ? campaign.winners
                        : [{}]; // at least one row per campaign

                    winners.forEach((w: any) => {
                        rows.push([
                            String(campaign.campaignId ?? ''),
                            campaign.campaignName ?? '',
                            campaign.startDate ?? '',
                            campaign.endDate ?? '',
                            campaign.campaignStatus ?? '',
                            String(campaign.noOfWinners ?? ''),
                            campaign.reportType ?? '',
                            campaign.createdByName ?? '',
                            campaign.approvedByName ?? '',
                            w.winnerName ?? '',
                            w.branchIdentifier ?? '',
                            w.winnerAccountNumber ?? '',
                            w.customerClassification ?? '',
                            String(w.accountBalance ?? ''),
                            String(w.salaryAmount ?? ''),
                            w.ccMaskedNo ?? '',
                            w.dcMaskedNo ?? '',
                            String(w.ccSpendingInPeriod ?? ''),
                            String(w.dcSpendingInPeriod ?? ''),
                            w.issDateCc ?? '',
                            String(w.numberofEntries ?? ''),
                        ]);
                    });
                });

                const csv = rows.map(r => r.map(cell => cell.includes(',') ? `"${cell}"` : cell).join(',')).join('\n');
                const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `${fileName}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                message.success('CSV downloaded!');

            } else {
                const wb = XLSX.utils.book_new();

                // Sheet 1: Campaigns summary
                const campaignRows = tableData.map((c: any) => ({
                    'Campaign ID':   c.campaignId,
                    'Campaign Name': c.campaignName,
                    'Start Date':    c.startDate,
                    'End Date':      c.endDate,
                    'Status':        c.campaignStatus,
                    'No Of Winners': c.noOfWinners,
                    'Report Type':   c.reportType,
                    'Created By':    c.createdByName,
                    'Approved By':   c.approvedByName,
                }));
                const ws1 = XLSX.utils.json_to_sheet(campaignRows);
                ws1['!cols'] = Object.keys(campaignRows[0] || {}).map(() => ({ wch: 20 }));
                XLSX.utils.book_append_sheet(wb, ws1, 'Campaigns');

                // Sheet 2: All winners flattened
                const winnerRows: any[] = [];
                tableData.forEach((c: any) => {
                    (Array.isArray(c.winners) ? c.winners : []).forEach((w: any) => {
                        winnerRows.push({
                            'Campaign ID':           c.campaignId,
                            'Campaign Name':         c.campaignName,
                            'Report Type':           c.reportType,
                            'Winner Name':           w.winnerName ?? '',
                            'Branch':                w.branchIdentifier ?? '',
                            'Account Number':        w.winnerAccountNumber ?? '',
                            'Classification':        w.customerClassification ?? '',
                            'Account Balance':       w.accountBalance ?? '',
                            'Salary Amount':         w.salaryAmount ?? '',
                            'CC Masked No':          w.ccMaskedNo ?? '',
                            'DC Masked No':          w.dcMaskedNo ?? '',
                            'CC Spending':           w.ccSpendingInPeriod ?? '',
                            'DC Spending':           w.dcSpendingInPeriod ?? '',
                            'CC Issue Date':         w.issDateCc ?? '',
                            'No. of Entries':        w.numberofEntries ?? '',
                        });
                    });
                });

                if (winnerRows.length > 0) {
                    const ws2 = XLSX.utils.json_to_sheet(winnerRows);
                    ws2['!cols'] = Object.keys(winnerRows[0]).map(() => ({ wch: 20 }));
                    XLSX.utils.book_append_sheet(wb, ws2, 'Winners');
                }

                XLSX.writeFile(wb, `${fileName}.xlsx`);
                message.success('Excel downloaded!');
            }
        } catch (error) {
            console.error('Export error:', error);
            message.error('Failed to export data');
        } finally {
            setSpinning(false);
        }
    };

    const exportMenuItems = [
        {
            key: 'csv',
            label: 'Export as CSV',
            onClick: () => handleExport('csv'),
        },
        {
            key: 'xls',
            label: 'Export as Excel (XLS)',
            onClick: () => handleExport('xls'),
        },
    ];

    const allColumns = [
        { title: 'Campaign Id', dataIndex: 'campaignId', key: 'campaignId', width: 80 },
        { title: 'Campaign Name', dataIndex: 'campaignName', key: 'campaignName', width: 150 },
        { title: 'Start Date', dataIndex: 'startDate', key: 'startDate', width: 120 },
        { title: 'End Date', dataIndex: 'endDate', key: 'endDate', width: 100 },
        { title: 'No Of Winners', dataIndex: 'noOfWinners', key: 'noOfWinners', width: 100 },
        { title: 'Status', dataIndex: 'campaignStatus', key: 'campaignStatus', width: 100 },
        { title: 'Created By', dataIndex: 'createdByName', key: 'createdByName', width: 100 },
        { title: 'Approved By', dataIndex: 'approvedByName', key: 'approvedByName', width: 100 },
        {
            title: 'Report Type',
            dataIndex: 'reportType',
            key: 'reportType',
            width: 160,
            render: (v: string) => v
                ? <Tag color="blue">{v.replace(/_/g, ' ')}</Tag>
                : <Tag color="default">N/A</Tag>,
        },
        // {
        //     title: 'Download',
        //     key: 'download',
        //     width: 130,
        //     render: (_: any, record: any) => (
        //         hasPermission(PERMISSIONS.AUDIT_REPORT_DOWNLOAD) ? (
        //             <Dropdown
        //                 trigger={['click']}
        //                 menu={{
        //                     items: [
        //                         {
        //                             key: 'xlsx',
        //                             label: 'Excel (.xlsx)',
        //                             icon: <DownloadOutlined />,
        //                             onClick: () => downloadCampaignReport(record, 'xlsx'),
        //                         },
        //                         {
        //                             key: 'csv',
        //                             label: 'CSV (.csv)',
        //                             icon: <DownloadOutlined />,
        //                             onClick: () => downloadCampaignReport(record, 'csv'),
        //                         },
        //                     ],
        //                 }}
        //             >
        //                 <Button size="small" icon={<DownloadOutlined />}>
        //                     <Space>Download<DownOutlined /></Space>
        //                 </Button>
        //             </Dropdown>
        //         ) : null
        //     ),
        // },
    ];

    return (
        <>
            <Header />
            <Spin spinning={spinning} fullscreen />
            <div className={style.Route}>
                <h5>Reporting</h5>
            </div>
            <form className={style.form} onSubmit={handleSubmit(onSearch)}>
                <div className={style.SearchTable}>
                    <div className={style.FormSetting}>
                        <div className={style.field}>
                            <FormInput Label='Start Date' placeholder='Start Date'
                                classInput={style.Input} className={style.inputContainer}
                                labelClass={style.Label} classError={style.Error}
                                name='StartDate' type='date' errors={errors} control={control} />
                        </div>
                        <div className={style.field}>
                            <FormInput Label='End Date' placeholder='End Date'
                                classInput={style.Input} className={style.inputContainer}
                                labelClass={style.Label} classError={style.Error}
                                name='EndDate' type='date' errors={errors} control={control} />
                        </div>
                        <div className={style.field}>
                            <FormInput Label='Campaign ID(s)' placeholder='e.g. 1,2,3'
                                classInput={style.Input} className={style.inputContainer}
                                labelClass={style.Label} classError={style.Error}
                                name='CampaignId' errors={errors} control={control} />
                        </div>
                    </div>
                    <div className={style.btnSetting}>
                        <Button type='primary' htmlType='submit' className={style.buttonSignIn}>Search</Button>
                        <Button type='primary' className={style.buttonSignIn} onClick={FilterClear}>Clear</Button>
                    </div>
                </div>
            </form>
            <div className={style.btnSetting}>
                {hasPermission(PERMISSIONS.AUDIT_REPORT_DOWNLOAD) && (
                    <Dropdown menu={{ items: exportMenuItems }} trigger={['click']}>
                        <Button className={style.buttonSignIn}>
                            <Space>
                                Export
                                <DownOutlined />
                            </Space>
                        </Button>
                    </Dropdown>
                )}
            </div>
            <div className={style.container}>
                <Table
                    columns={allColumns}
                    dataSource={tableData.map((item: any, i: number) => ({ ...item, key: item.campaignId ?? i }))}
                    pagination={false}
                    size="small"
                />
                <Pagination
                    align="end"
                    showTotal={(total) => `Total ${total} items`}
                    current={currentPage}
                    total={totalRecords}
                    pageSize={pageSize}
                    pageSizeOptions={[9, 20, 50]}
                    showSizeChanger
                    onChange={(page, newSize) => {
                        setCurrentPage(page);
                        setPageSize(newSize);
                    }}
                    style={{ marginTop: 16 }}
                />
            </div>
        </>
    );
};

function mapStateToProps(state: any) {
    return { Reporting_Red: state.Reporting_Red };
}

export default connect(mapStateToProps, Action)(MultiViewTable);
